import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getDefaults,
  updateDefault,
  fetchEstadoCotacaoOnline,
  getProdutoDepara,
  deleteProdutoDepara,
  getUsuarioDepara,
  vincularUsuarioNasajon,
  rotuloOrigemDoVinculo,
  removerUsuarioNasajon,
  searchUsuariosNasajon,
  SettingsDefaults,
} from '../../../api/orcamentoCiliaApi';
import { getUsers } from '../../../api/usersApi';
import { queryKeys } from '../../../infrastructure/react-query/queryKeys';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import HistoricoConfiguracaoModal from '../components/HistoricoConfiguracaoModal';
import PausaNasajonCard from '../components/PausaNasajonCard';
import '../../recepcao/pages/Recepcao.css';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import './OrcamentoCiliaConfig.css';

const OrcamentoCiliaConfig: React.FC = () => {
  const queryClient = useQueryClient();

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  // Montagem condicional (padrão de modais do projeto): o estado interno nasce a cada
  // abertura, então o histórico é sempre buscado fresco.
  const [isHistoricoOpen, setIsHistoricoOpen] = useState(false);
  const [isDefaultsHelpOpen, setIsDefaultsHelpOpen] = useState(false);
  const [searchDepara, setSearchDepara] = useState('');
  const [deparaPage, setDeparaPage] = useState(1);
  const deparaLimit = 10;
  const [deparaToDelete, setDeparaToDelete] = useState<string | null>(null);

  // De-para de usuário (at-11): quem assina a requisição no ERP.
  const [usuarioSoacSelecionado, setUsuarioSoacSelecionado] = useState('');
  const [buscaUsuarioNasajon, setBuscaUsuarioNasajon] = useState('');
  const [usuarioNasajonSelecionado, setUsuarioNasajonSelecionado] = useState('');
  const [vinculoParaRemover, setVinculoParaRemover] = useState<string | null>(null);

  // Queries
  const { data: defaults, isLoading: isLoadingDefaults, refetch: refetchDefaults } = useQuery({
    queryKey: queryKeys.orcamentoCilia.defaults,
    queryFn: getDefaults,
  });

  const { data: deparaData, isLoading: isLoadingDepara } = useQuery({
    queryKey: queryKeys.orcamentoCilia.produtoDepara({ search: searchDepara, page: deparaPage, limit: deparaLimit }),
    queryFn: () => getProdutoDepara({ search: searchDepara, page: deparaPage, limit: deparaLimit }),
  });

  const deparaTotalPages = deparaData ? Math.max(1, Math.ceil(deparaData.total / deparaLimit)) : 1;

  // ── De-para de usuário (at-11) ──

  const {
    data: usuarioDepara,
    isLoading: isLoadingUsuarioDepara,
    isError: erroUsuarioDepara,
  } = useQuery({
    queryKey: queryKeys.orcamentoCilia.usuarioDepara,
    queryFn: getUsuarioDepara,
  });

  /*
   * As pessoas do sistema que podem concluir uma conferência. O filtro por papel é da TELA, não do
   * backend: oferecer a lista inteira faria o ADMIN vincular quem nunca vai concluir orçamento —
   * e cada credencial do ERP só pode ser usada por uma pessoa.
   */
  const {
    data: usuariosSoac,
    isLoading: carregandoUsuariosSoac,
    isError: erroUsuariosSoac,
  } = useQuery({
    queryKey: ['users', 'orcamento-cilia-depara'],
    queryFn: () => getUsers({ page: 1, limit: 200, status: 'ATIVOS', userTypes: 'ORCAMENTO,ADMIN' }),
  });

  /*
   * ⚠️ Só busca a partir de 3 caracteres: a rota do ERP responde a consulta aberta com centenas de
   * usuários, e um `select` de 147 nomes não ajuda ninguém a achar a pessoa certa.
   */
  const {
    data: usuariosNasajon,
    isFetching: buscandoUsuariosNasajon,
    isError: erroUsuariosNasajon,
  } = useQuery({
    queryKey: ['orcamento-cilia', 'usuarios-nasajon', buscaUsuarioNasajon],
    queryFn: () => searchUsuariosNasajon(buscaUsuarioNasajon.trim()),
    enabled: buscaUsuarioNasajon.trim().length >= 3,
  });

  const vincularUsuarioMutation = useMutation({
    mutationFn: vincularUsuarioNasajon,
    onSuccess: () => {
      toast.success('Vínculo salvo. As requisições desta pessoa passam a ser criadas com o usuário do ERP.');
      setUsuarioSoacSelecionado('');
      setUsuarioNasajonSelecionado('');
      setBuscaUsuarioNasajon('');
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.usuarioDepara });
    },
    onError: (err: any) => {
      // O backend nomeia quem já usa a credencial (`USUARIO_NASAJON_JA_VINCULADO`) — a mensagem
      // dele é mais útil que qualquer texto genérico daqui.
      toast.error(err.message || 'Não foi possível vincular o usuário do Nasajon.');
    },
  });

  const removerUsuarioMutation = useMutation({
    mutationFn: removerUsuarioNasajon,
    onSuccess: () => {
      toast.success('Vínculo removido.');
      setVinculoParaRemover(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.usuarioDepara });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Não foi possível remover o vínculo.');
    },
  });

  // Ex: excluir o último item da última página — recua para a página que ainda existe
  useEffect(() => {
    if (deparaData && deparaPage > deparaTotalPages) {
      setDeparaPage(deparaTotalPages);
    }
  }, [deparaData, deparaPage, deparaTotalPages]);

  // at-15: gasto do mês da cotação online, ao lado do teto.
  const estadoCotacao = useQuery({
    queryKey: ['orcamento-cilia', 'cotacao-online', 'estado'],
    queryFn: fetchEstadoCotacaoOnline,
  });

  // Mutations
  const updateDefaultMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => updateDefault(key, value),
    onSuccess: () => {
      toast.success('Padrão do produto atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.defaults });
      queryClient.invalidateQueries({ queryKey: ['orcamento-cilia', 'cotacao-online', 'estado'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Erro ao atualizar padrão');
      refetchDefaults();
    }
  });

  const deleteDeparaMutation = useMutation({
    mutationFn: (codigoCilia: string) => deleteProdutoDepara(codigoCilia),
    onSuccess: () => {
      toast.success('Vínculo de produto excluído!');
      setDeparaToDelete(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.all });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Erro ao excluir vínculo');
      setDeparaToDelete(null);
    }
  });

  const handleDefaultBlur = (key: string, currentValue: string, newValue: string) => {
    if (newValue === currentValue) return;
    updateDefaultMutation.mutate({ key, value: newValue });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchDepara(e.target.value);
    setDeparaPage(1);
  };

  const handleDeleteConfirm = () => {
    if (deparaToDelete) {
      deleteDeparaMutation.mutate(deparaToDelete);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  // Conteúdo de ajuda dos Defaults — usado no "?" do card e no drawer geral da tela
  const defaultsHelpConteudo = (
    <div>
      <p><strong>Por que isso está aqui?</strong></p>
      <p>
        Quando uma peça do orçamento não existe no ERP, o sistema a cadastra no Nasajon ao
        concluir o orçamento. O Nasajon <strong>exige</strong> unidade, grupo de inventário,
        origem da mercadoria e NCM nesse cadastro — e o XML do Cilia <strong>não traz</strong>{' '}
        esses dados. Sem os defaults, seria preciso digitá-los peça por peça.
      </p>
      <p><strong>O que cada campo faz:</strong></p>
      <ul>
        <li><strong>Unidade Padrão:</strong> unidade de medida aplicada às peças novas (Ex: UN).</li>
        <li><strong>Grupo de Inventário Padrão:</strong> grupo em que a peça entra no ERP (Ex: mercadorias).</li>
        <li><strong>Origem de Mercadoria Padrão:</strong> origem fiscal da peça (Ex: nacional).</li>
        <li>
          <strong>NCM Padrão (peça nova):</strong> NCM enviado no cadastro de peças novas. Por decisão
          de negócio (28/07/2026), segue o padrão usado hoje pela casa — a classificação fiscal fina
          fica fora do escopo do módulo no MVP.
        </li>
        <li>
          <strong>Referência de preço — janela de compras (meses):</strong> período usado para calcular a
          média ponderada das compras da peça no ERP, que é a referência de preço da conferência (default:
          6 meses). Peça sem compra no período fica sem referência.
        </li>
        <li>
          <strong>Alerta de preço — margem sobre a referência (%):</strong> o alerta da conferência só dispara
          quando o valor da peça passa da média de compras mais esta margem. A média é histórica e "atrasa"
          em relação ao preço de hoje — sem margem, o alerta dispararia quase sempre.
        </li>
      </ul>
      <p>
        Os valores iniciais refletem o cadastro real da Associação no Nasajon, então raramente
        precisam mudar; serem configuráveis evita mexer no código se o ERP mudar a nomenclatura.
        Alterações são gravadas automaticamente ao sair do campo.
      </p>
    </div>
  );

  // Seções de ajuda para "Como Funciona"
  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'Chamadas ao Nasajon',
      conteudo: (
        <div>
          <p>
            Duas rotinas deste módulo consultam o Nasajon sozinhas, a cada 10 minutos: o aviso de peças
            pendentes de verificação e a sincronização do desfecho das requisições. O card{' '}
            <strong>Chamadas ao Nasajon</strong> pausa as duas — por exemplo, quando é preciso parar o
            consumo da API do ERP.
          </p>
          <p>
            A pausa <strong>não</strong> trava a tela: importar, concluir e reverificar continuam
            funcionando. E ela vale só para este módulo; o Dashboard de Compras tem a sua, em
            Administração do Sistema → Compras.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Defaults do Produto Nasajon',
      conteudo: (
        <div>
          <p>
            Valores padrão (unidade, grupo de inventário, origem e NCM) aplicados automaticamente às
            peças novas criadas no ERP Nasajon ao concluir um orçamento — o XML do Cilia não traz
            esses dados, que são obrigatórios no ERP.
          </p>
          <p>
            A explicação completa está no <strong>"?"</strong> ao lado do título da seção, na própria tela.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Requisição no Nasajon',
      conteudo: (
        <div>
          <p>
            No "Concluir e Enviar", quem conclui informa se o veículo está em <strong>oficina interna ou
            parceira</strong> e se é <strong>carro ou moto</strong>. Para cada uma das quatro combinações,
            esta seção guarda o <strong>local de uso</strong> — é dele que o Nasajon tira o centro de custo e a
            classificação financeira — e o <strong>local de estoque</strong>.
          </p>
          <p>
            O local de estoque é informado à parte porque, no Nasajon, todos os locais de uso de oficina vêm
            com "Peças Internas" como padrão — inclusive os de oficina externa.
          </p>
          <p>
            Combinação em branco faz a conclusão ser <strong>recusada</strong> com uma mensagem, sem criar
            nada no Nasajon. O mesmo acontece se o código informado não existir lá, estiver bloqueado ou vier
            sem centro de custo. A mudança vale para as próximas conclusões; requisições já criadas não mudam.
          </p>
          <p>
            O <strong>número a partir do qual o sistema procura</strong> é ajustado sozinho a cada requisição
            criada: o número da requisição segue a sequência do Nasajon, e este campo só diz de onde começar a
            procurar o próximo livre. Normalmente não precisa ser mexido.
          </p>
        </div>
      ),
    },
    {
      titulo: 'De-para de Produtos (código Cilia → produto Nasajon)',
      conteudo: (
        <div>
          <p>
            Esta tabela é o <strong>aprendizado do vínculo de peças</strong>: quando o orçamentista informa o
            código do produto do Nasajon (ou usa o "Reconhecer") na conferência, o sistema memoriza o par —
            nas próximas importações, a mesma peça do Cilia já chega vinculada ao produto certo do ERP.
          </p>
          <p>
            <strong>Importante:</strong> novos registros nascem da conferência, não daqui. Excluir um vínculo
            força um novo aprendizado na próxima importação daquela peça.
          </p>
          <p>
            O reconhecimento e a busca consultam o <strong>cadastro do Nasajon em tempo real</strong> — não há
            mais cópia local para sincronizar.
          </p>
        </div>
      )
    }
  ];

  return (
    <div className="ConteDo recepcao-conte-do" style={{ paddingBottom: '100px', gap: '24px' }}>
      {/*
        Cabeçalho. O botão "Voltar" saiu em 12/08/2026, junto com a mudança de lugar da
        tela: ele levava à lista de orçamentos, que deixou de ser de onde se chega aqui.
        Não foi trocado por um "Voltar" ao hub de propósito — quem configura um módulo só
        é redirecionado pelo hub de volta para esta tela, então o botão pareceria quebrado.
        A saída é o menu lateral, como na configuração de Cotações.
      */}
      <div className="recepcao-header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingTop: '40px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <div className="recepcao-title">Configurações de Requisições</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/*
            Histórico de alterações (at-05): os defaults decidem o resultado de toda
            importação seguinte, e sem isto uma troca de valor some sem deixar rastro
            visível. Molde do botão equivalente na tela de Usuários do SOAC (at-69).
          */}
          <button type="button" className="cilia-btn" onClick={() => setIsHistoricoOpen(true)}>
            Histórico de alterações
          </button>

          <button
            className="help-circle-btn"
            onClick={() => setIsHelpOpen(true)}
            title="Como Funciona"
          >
            ?
          </button>
        </div>
      </div>

      {/* transversal/at-99 — a pausa das rotinas que chamam o Nasajon. Carga própria: falhar aqui
          não esconde o resto da tela, e falhar no resto não esconde a pausa. */}
      <PausaNasajonCard />

      {/* Card Defaults */}
      <div className="cilia-config-card" style={{ width: '100%' }}>
        <div className="config-card-header">
          <div className="config-card-title-row">
            <h2>Defaults do Produto Nasajon</h2>
            <button
              className="config-help-btn"
              onClick={() => setIsDefaultsHelpOpen(true)}
              title="Entenda para que servem os defaults"
              aria-label="Ajuda sobre os Defaults do Produto Nasajon"
            >
              ?
            </button>
          </div>
        </div>
        <div className="config-card-body">
          {isLoadingDefaults ? (
            <div className="config-loading">Carregando padrões...</div>
          ) : !defaults ? (
            <div className="config-error">Erro ao carregar padrões do ERP.</div>
          ) : (
            <div className="config-defaults-grid">
              <div className="config-form-group">
                <label htmlFor="default-unidade">Unidade Padrão</label>
                <input
                  id="default-unidade"
                  type="text"
                  defaultValue={defaults.DEFAULT_UNIDADE}
                  onBlur={(e) => handleDefaultBlur('DEFAULT_UNIDADE', defaults.DEFAULT_UNIDADE, e.target.value)}
                  disabled={updateDefaultMutation.isPending}
                  className="cilia-input"
                  placeholder="Ex: UN"
                />
              </div>

              <div className="config-form-group">
                <label htmlFor="default-grupo">Grupo de Inventário Padrão</label>
                <input
                  id="default-grupo"
                  type="text"
                  defaultValue={defaults.DEFAULT_GRUPO_INVENTARIO}
                  onBlur={(e) => handleDefaultBlur('DEFAULT_GRUPO_INVENTARIO', defaults.DEFAULT_GRUPO_INVENTARIO, e.target.value)}
                  disabled={updateDefaultMutation.isPending}
                  className="cilia-input"
                  placeholder="Ex: PECA"
                />
              </div>

              <div className="config-form-group">
                <label htmlFor="default-origem">Origem de Mercadoria Padrão</label>
                <input
                  id="default-origem"
                  type="text"
                  defaultValue={defaults.DEFAULT_ORIGEM_MERCADORIA}
                  onBlur={(e) => handleDefaultBlur('DEFAULT_ORIGEM_MERCADORIA', defaults.DEFAULT_ORIGEM_MERCADORIA, e.target.value)}
                  disabled={updateDefaultMutation.isPending}
                  className="cilia-input"
                  placeholder="Ex: 0"
                />
              </div>

              <div className="config-form-group">
                <label htmlFor="default-ncm">NCM Padrão (peça nova)</label>
                <input
                  id="default-ncm"
                  type="text"
                  defaultValue={defaults.DEFAULT_NCM}
                  onBlur={(e) => handleDefaultBlur('DEFAULT_NCM', defaults.DEFAULT_NCM, e.target.value)}
                  disabled={updateDefaultMutation.isPending}
                  className="cilia-input font-mono"
                  placeholder="Ex: 10000000"
                />
              </div>

              <div className="config-form-group">
                <label htmlFor="default-media-meses">Referência de preço — janela de compras (meses)</label>
                <input
                  id="default-media-meses"
                  type="text"
                  defaultValue={defaults.MEDIA_COMPRAS_MESES}
                  onBlur={(e) =>
                    handleDefaultBlur('MEDIA_COMPRAS_MESES', defaults.MEDIA_COMPRAS_MESES, e.target.value)
                  }
                  disabled={updateDefaultMutation.isPending}
                  className="cilia-input"
                  placeholder="Ex: 6"
                />
              </div>

              <div className="config-form-group">
                <label htmlFor="default-margem-pmc">Alerta de preço — margem sobre a referência (%)</label>
                <input
                  id="default-margem-pmc"
                  type="text"
                  defaultValue={defaults.PMC_ALERTA_MARGEM_PCT}
                  onBlur={(e) =>
                    handleDefaultBlur('PMC_ALERTA_MARGEM_PCT', defaults.PMC_ALERTA_MARGEM_PCT, e.target.value)
                  }
                  disabled={updateDefaultMutation.isPending}
                  className="cilia-input"
                  placeholder="Ex: 50"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/*
        COTAÇÃO ONLINE (at-15): o teto de gasto mensal é personalizável aqui (decisão do Victor,
        20/10/2026). Sem valor salvo, nada é pesquisado — o deploy nasce inerte.
      */}
      <div className="cilia-config-card" style={{ width: '100%' }}>
        <div className="config-card-header">
          <div className="config-card-title-row">
            <h2>Cotação online</h2>
          </div>
          <p className="config-subtitle">
            Ao concluir uma requisição, o sistema pesquisa o preço de cada peça na internet com inteligência artificial, que é
            cobrada por uso. O <strong>teto</strong> limita o gasto do mês: atingido, as pesquisas param até o mês seguinte ou até o
            teto aumentar. Vazio ou zero deixa a cotação online desligada.
          </p>
        </div>
        <div className="config-card-body">
          {isLoadingDefaults ? (
            <div className="config-loading">Carregando configuração...</div>
          ) : !defaults ? (
            <div className="config-error">Erro ao carregar a configuração.</div>
          ) : (
            <div className="config-defaults-grid">
              <div className="config-form-group">
                <label htmlFor="default-teto-cotacao">Teto de gasto mensal (US$)</label>
                <input
                  id="default-teto-cotacao"
                  type="text"
                  defaultValue={defaults.COTACAO_ONLINE_TETO_MENSAL_USD}
                  onBlur={(e) => {
                    // Apagar o campo desliga (vira "0": o backend não aceita valor vazio).
                    const v = e.target.value.trim();
                    if (!v && !defaults.COTACAO_ONLINE_TETO_MENSAL_USD) return;
                    handleDefaultBlur('COTACAO_ONLINE_TETO_MENSAL_USD', defaults.COTACAO_ONLINE_TETO_MENSAL_USD, v || '0');
                  }}
                  disabled={updateDefaultMutation.isPending}
                  className="cilia-input"
                  placeholder="Ex: 300"
                />
              </div>
              <div className="config-form-group">
                <label htmlFor="cotacao-gasto-mes">Gasto estimado neste mês</label>
                <input
                  id="cotacao-gasto-mes"
                  type="text"
                  className="cilia-input"
                  disabled
                  // 🔴 Falha de carga nunca vira "US$ 0,00" (at-60): diz que não conseguiu consultar.
                  value={
                    estadoCotacao.isError
                      ? 'Não foi possível consultar'
                      : estadoCotacao.data
                        ? `US$ ${estadoCotacao.data.gastoNoMesUsd.toFixed(2)}${
                            estadoCotacao.data.tetoMensalUsd != null
                              ? ` (${Math.round((estadoCotacao.data.gastoNoMesUsd / estadoCotacao.data.tetoMensalUsd) * 100)}% do teto)`
                              : ''
                          }`
                        : 'Carregando...'
                  }
                />
              </div>
              <div className="config-form-group">
                <label htmlFor="cotacao-situacao">Situação</label>
                <input
                  id="cotacao-situacao"
                  type="text"
                  className="cilia-input"
                  disabled
                  title={estadoCotacao.data?.motivoParado ?? undefined}
                  value={
                    estadoCotacao.isError
                      ? 'Não foi possível consultar'
                      : estadoCotacao.data
                        ? estadoCotacao.data.motivoParado
                          ? 'Parada'
                          : 'Ligada'
                        : 'Carregando...'
                  }
                />
              </div>
            </div>
          )}
          {estadoCotacao.data?.motivoParado && <p className="config-subtitle">{estadoCotacao.data.motivoParado}</p>}
          {estadoCotacao.isError && (
            <p className="erro-de-tela">Não foi possível consultar o gasto do mês. Recarregue a página; se persistir, acione a TI.</p>
          )}
        </div>
      </div>

      {/*
        DESTINO DA REQUISIÇÃO NO NASAJON (at-14, ADR 0009). O local de uso de cada combinação faz o
        ERP preencher centro de custo e classificação financeira; o local de estoque vai à parte,
        porque o padrão de TODOS os locais de uso de oficina é "Peças Internas" (medido em 19/10/2026).
        Os códigos ficam aqui, e não no código-fonte, porque o cadastro do ERP tem irmãos parecidos
        com classificação diferente — quem escolhe o certo é Compras, e trocar não exige deploy.
      */}
      <div className="cilia-config-card" style={{ width: '100%' }}>
        <div className="config-card-header">
          <div className="config-card-title-row">
            <h2>Requisição no Nasajon</h2>
          </div>
          <p className="config-subtitle">
            Para cada combinação de oficina e veículo, o <strong>código do local de uso</strong> (que define o centro de
            custo e a classificação financeira) e o <strong>código do local de estoque</strong>. Combinação vazia faz o
            "Concluir e Enviar" recusar, com mensagem — nada é criado no Nasajon sem destino.
          </p>
        </div>
        <div className="config-card-body">
          {isLoadingDefaults ? (
            <div className="config-loading">Carregando configuração...</div>
          ) : !defaults ? (
            <div className="config-error">Erro ao carregar a configuração.</div>
          ) : (
            <div className="config-defaults-grid">
              {(
                [
                  ['LOCAL_USO_INTERNA_CARRO', 'Oficina interna, carro — local de uso'],
                  ['LOCAL_ESTOQUE_INTERNA_CARRO', 'Oficina interna, carro — local de estoque'],
                  ['LOCAL_USO_EXTERNA_CARRO', 'Oficina parceira, carro — local de uso'],
                  ['LOCAL_ESTOQUE_EXTERNA_CARRO', 'Oficina parceira, carro — local de estoque'],
                  ['LOCAL_USO_INTERNA_MOTO', 'Oficina interna, moto — local de uso'],
                  ['LOCAL_ESTOQUE_INTERNA_MOTO', 'Oficina interna, moto — local de estoque'],
                  ['LOCAL_USO_EXTERNA_MOTO', 'Oficina parceira, moto — local de uso'],
                  ['LOCAL_ESTOQUE_EXTERNA_MOTO', 'Oficina parceira, moto — local de estoque'],
                  ['CNPJ_OFICINA_PROPRIA', 'CNPJ da oficina própria (só números)'],
                  ['REQUISICAO_NUMERO_BASE', 'Número a partir do qual o sistema procura o próximo'],
                ] as Array<[keyof SettingsDefaults, string]>
              ).map(([chave, rotulo]) => (
                <div className="config-form-group" key={chave}>
                  <label htmlFor={`destino-${chave}`}>{rotulo}</label>
                  <input
                    id={`destino-${chave}`}
                    type="text"
                    defaultValue={defaults[chave] ?? ''}
                    onBlur={(e) => handleDefaultBlur(chave, defaults[chave] ?? '', e.target.value.trim())}
                    disabled={updateDefaultMutation.isPending}
                    className="cilia-input"
                    placeholder="Não configurado"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/*
        QUEM ASSINA A REQUISIÇÃO NO NASAJON (at-11, ADR 0007 decisão 3).

        A requisição de compra é criada com o usuário **nominal** de quem conferiu o orçamento, não
        com um usuário de serviço: é um documento que um gestor vai aprovar, e apagar a autoria ali
        inviabilizaria a própria aprovação.

        Desde a orcamento-cilia/at-13 (16/10/2026, decisão do Victor) o vínculo é AUTOMÁTICO pelo
        e-mail empresarial, feito ao abrir a tela de Requisições, e só com casamento único. Este card
        é a saída manual para quem o automático não alcança (e-mail trocado, digitado diferente,
        ou dois usuários do ERP com o mesmo e-mail).
      */}
      <div className="cilia-config-card" style={{ width: '100%' }}>
        <div className="config-card-header flex-header">
          <div>
            <h2>Quem assina a requisição no Nasajon</h2>
            <span className="config-subtitle">
              Cada pessoa que conclui uma conferência precisa estar vinculada ao usuário dela no ERP — é
              em nome dele que a requisição é criada. Sem o vínculo, a conclusão é recusada antes de
              criar qualquer coisa lá. O vínculo é feito sozinho quando o e-mail da pessoa aqui é
              igual ao do Nasajon; use este cadastro para quem o e-mail não bate.
            </span>
          </div>
        </div>
        <div className="config-card-body" style={{ padding: '14px 0 0 0' }}>
          {/* Formulário de vínculo: escolhe a pessoa do SOAC e busca a credencial no ERP. */}
          <div className="usuario-depara-form">
            <div className="config-form-group">
              <label htmlFor="vinculo-pessoa-soac">Pessoa do sistema</label>
              <select
                id="vinculo-pessoa-soac"
                className="cilia-select"
                value={usuarioSoacSelecionado}
                onChange={(e) => setUsuarioSoacSelecionado(e.target.value)}
                disabled={carregandoUsuariosSoac}
              >
                <option value="">Selecione...</option>
                {(usuariosSoac?.users ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName || u.username}
                  </option>
                ))}
              </select>
              {erroUsuariosSoac && (
                <span className="erro-de-tela">
                  Não foi possível carregar as pessoas do sistema. Recarregue a página; se persistir,
                  acione a TI.
                </span>
              )}
            </div>

            <div className="config-form-group">
              <label htmlFor="vinculo-busca-nasajon">Usuário no Nasajon</label>
              <input
                id="vinculo-busca-nasajon"
                type="text"
                className="cilia-input"
                placeholder="Buscar por nome ou e-mail no ERP..."
                value={buscaUsuarioNasajon}
                onChange={(e) => setBuscaUsuarioNasajon(e.target.value)}
              />
              {/*
                ⚠️ "Nenhum resultado" e "não consegui perguntar" levam a conclusões opostas: a
                primeira manda procurar outro nome, a segunda manda acionar a TI (regra da at-60).
              */}
              {erroUsuariosNasajon ? (
                <span className="erro-de-tela">
                  Não foi possível consultar os usuários no Nasajon. Tente de novo em instantes.
                </span>
              ) : buscandoUsuariosNasajon ? (
                <span className="config-subtitle">Buscando no Nasajon...</span>
              ) : buscaUsuarioNasajon.trim().length >= 3 && (usuariosNasajon ?? []).length === 0 ? (
                <span className="config-subtitle">Nenhum usuário encontrado com esse nome no ERP.</span>
              ) : null}
              {(usuariosNasajon ?? []).length > 0 && (
                <select
                  className="cilia-select"
                  value={usuarioNasajonSelecionado}
                  onChange={(e) => setUsuarioNasajonSelecionado(e.target.value)}
                >
                  <option value="">Selecione o usuário do ERP...</option>
                  {(usuariosNasajon ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.login || u.id}
                      {u.email ? ` — ${u.email}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <button
              className="cilia-btn confirm"
              disabled={
                !usuarioSoacSelecionado || !usuarioNasajonSelecionado || vincularUsuarioMutation.isPending
              }
              onClick={() => {
                const pessoa = (usuariosSoac?.users ?? []).find((u) => u.id === usuarioSoacSelecionado);
                vincularUsuarioMutation.mutate({
                  userIdSoac: usuarioSoacSelecionado,
                  nomeSoac: pessoa?.fullName || pessoa?.username || null,
                  nasajonUserId: usuarioNasajonSelecionado,
                });
              }}
            >
              {vincularUsuarioMutation.isPending ? 'Vinculando...' : 'Vincular'}
            </button>
          </div>

          {isLoadingUsuarioDepara ? (
            <div className="config-loading">Carregando vínculos...</div>
          ) : erroUsuarioDepara ? (
            // A lista vazia por erro seria lida como "ninguém está vinculado" — e a reação a isso
            // é refazer vínculos que já existem.
            <div className="erro-de-tela">
              Não foi possível carregar os vínculos de usuário. Recarregue a página; se persistir,
              acione a TI.
            </div>
          ) : (usuarioDepara ?? []).length === 0 ? (
            <div className="config-empty">
              Ninguém vinculado ainda. Enquanto não houver vínculo, nenhuma conferência pode ser
              concluída.
            </div>
          ) : (
            <div className="TabelaScroll" style={{ width: '100%' }}>
              <div
                className="TabelaContent"
                style={{ ['--table-columns' as any]: '1.5fr 1.5fr 2fr 1fr 120px', width: '100%', minWidth: '840px' }}
              >
                {/* Mesma estrutura de cabeçalho da tabela de produtos, abaixo (`Placa > TTulo > Opcoes`):
                    é ela que o CSS da tabela do sistema estiliza — com `Item` no cabeçalho, as colunas
                    desalinham e o texto cola na borda da faixa. */}
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      <div className="Opcoes">Pessoa do sistema</div>
                      <div className="Opcoes">Usuário no Nasajon</div>
                      <div className="Opcoes">E-mail no ERP</div>
                      <div className="Opcoes">Origem</div>
                      <div className="Opcoes" style={{ textAlign: 'right', paddingRight: '36px' }}>Ações</div>
                    </div>
                  </div>
                </div>
                {(usuarioDepara ?? []).map((v) => (
                  <div className="Tabela" key={v.userIdSoac}>
                    <div className="Item">
                      <div className="Data">{v.nomeSoac || v.userIdSoac}</div>
                    </div>
                    <div className="Item">
                      <div className="Data">{v.nasajonNome || v.nasajonLogin || v.nasajonUserId}</div>
                    </div>
                    <div className="Item">
                      <div className="Data">{v.nasajonEmail || '—'}</div>
                    </div>
                    <div className="Item">
                      <div className="Data">{rotuloOrigemDoVinculo(v.origem)}</div>
                    </div>
                    <div className="Item" style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '36px' }}>
                      <div className="Data" style={{ display: 'block', width: 'auto' }}>
                        <button
                          className="cilia-delete-btn"
                          onClick={() => setVinculoParaRemover(v.userIdSoac)}
                          title="Remover vínculo"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card De-para de Produtos */}
      <div className="cilia-config-card" style={{ width: '100%' }}>
        <div className="config-card-header flex-header">
          <div>
            <h2>De-para de Produtos (Cilia → Nasajon)</h2>
            <span className="config-subtitle">
              Vínculos aprendidos na conferência — a mesma peça do Cilia chega reconhecida nas próximas
              importações. Novas entradas nascem da conferência, não daqui.
            </span>
          </div>
          <input
            type="text"
            placeholder="Buscar por código ou descrição..."
            value={searchDepara}
            onChange={handleSearchChange}
            className="cilia-input config-search"
          />
        </div>
        <div className="config-card-body" style={{ padding: '14px 0 0 0' }}>
          {isLoadingDepara ? (
            <div className="config-loading">Carregando vínculos...</div>
          ) : !deparaData || !Array.isArray(deparaData.data) || deparaData.total === 0 ? (
            <div className="config-empty">Nenhum vínculo de produto aprendido ainda.</div>
          ) : (
            <div className="TabelaScroll" style={{ width: '100%' }}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: '1fr 1fr 2fr 1fr 120px',
                  width: '100%',
                  minWidth: '760px',
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      <div className="Opcoes">Código Cilia</div>
                      <div className="Opcoes">Código Nasajon</div>
                      <div className="Opcoes">Produto no ERP</div>
                      <div className="Opcoes">Atualizado em</div>
                      <div className="Opcoes" style={{ textAlign: 'right', paddingRight: '20px' }}>Ações</div>
                    </div>
                  </div>
                </div>

                {deparaData.data.map((item) => (
                  <div key={item.codigoCilia} className="Tabela">
                    <div className="Item font-mono">
                      <div className="Data">{item.codigoCilia}</div>
                    </div>
                    <div className="Item font-mono">
                      <div className="Data">{item.codigoNasajon || '—'}</div>
                    </div>
                    <div className="Item">
                      <div className="Data">{item.especificacao || '—'}</div>
                    </div>
                    <div className="Item">
                      <div className="Data">{formatDate(item.updatedAt)}</div>
                    </div>
                    <div className="Item" style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '20px' }}>
                      <div className="Data" style={{ display: 'block', width: 'auto' }}>
                        <button
                          className="cilia-delete-btn"
                          onClick={() => setDeparaToDelete(item.codigoCilia)}
                          title="Excluir vínculo"
                        >
                          Excluir
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {deparaData && deparaData.total > deparaLimit && (
          <div className="PaginaO" style={{ borderTop: '1px solid var(--tema-linha, #EDEDED)', width: '100%', boxSizing: 'border-box' }}>
            <div className="Frame1000003950">
              <div
                className="Anterior"
                onClick={() => deparaPage > 1 && setDeparaPage((prev) => prev - 1)}
                style={{
                  cursor: deparaPage === 1 ? 'not-allowed' : 'pointer',
                  color: deparaPage === 1 ? '#BEBEBE' : '#ea5d0e',
                  fontWeight: deparaPage === 1 ? '400' : '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <img src={deparaPage === 1 ? ArrowLeftGray : ArrowLeftOrange} alt="Anterior" />
                Anterior
              </div>
            </div>

            <div className="Group20">
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: 'var(--tema-texto-2, #5E5E5E)' }}>
                Página <strong>{deparaPage}</strong> de {deparaTotalPages}
              </span>
            </div>

            <div className="Frame1000003949">
              <div
                className="PrXima"
                onClick={() => deparaPage < deparaTotalPages && setDeparaPage((prev) => prev + 1)}
                style={{
                  cursor: deparaPage === deparaTotalPages ? 'not-allowed' : 'pointer',
                  color: deparaPage === deparaTotalPages ? '#BEBEBE' : '#ea5d0e',
                  fontWeight: deparaPage === deparaTotalPages ? '400' : '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                Próxima
                <img src={deparaPage === deparaTotalPages ? ArrowRightGray : ArrowRightOrange} alt="Próxima" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Confirmação Exclusão */}
      {deparaToDelete && (
        <div
          className="config-modal-overlay"
          onClick={(e) => {
            // Fecha só no clique direto no overlay (fora da caixa) e nunca no meio da exclusão
            if (e.target === e.currentTarget && !deleteDeparaMutation.isPending) {
              setDeparaToDelete(null);
            }
          }}
        >
          <div className="config-modal-box">
            <div className="config-modal-header">
              <h3>Excluir Vínculo de Produto</h3>
            </div>
            <div className="config-modal-body">
              <p>Tem certeza de que deseja remover o vínculo aprendido para a peça <strong>{deparaToDelete}</strong>?</p>
              <p>O sistema deixará de reconhecer automaticamente esta peça em importações futuras — até um novo vínculo ser feito na conferência.</p>
            </div>
            <div className="config-modal-footer">
              <button
                className="cilia-btn cancel"
                onClick={() => setDeparaToDelete(null)}
                disabled={deleteDeparaMutation.isPending}
              >
                Cancelar
              </button>
              <button
                className="cilia-btn confirm danger"
                onClick={handleDeleteConfirm}
                disabled={deleteDeparaMutation.isPending}
              >
                {deleteDeparaMutation.isPending ? 'Excluindo...' : 'Excluir Vínculo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        Remover vínculo pede confirmação porque a consequência não é óbvia na hora: sem ele, a
        pessoa **não consegue mais concluir conferência** — a conclusão é recusada antes de criar
        qualquer coisa no ERP.
      */}
      {vinculoParaRemover && (
        <div
          className="config-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !removerUsuarioMutation.isPending) setVinculoParaRemover(null);
          }}
        >
          <div className="config-modal-box">
            <div className="config-modal-header">
              <h3>Remover vínculo com o Nasajon</h3>
            </div>
            <div className="config-modal-body">
              <p>
                Sem o vínculo, esta pessoa <strong>não conseguirá concluir conferências</strong>: a
                requisição é criada em nome do usuário dela no ERP.
              </p>
            </div>
            <div className="config-modal-footer">
              <button
                className="cilia-btn cancel"
                onClick={() => setVinculoParaRemover(null)}
                disabled={removerUsuarioMutation.isPending}
              >
                Cancelar
              </button>
              <button
                className="cilia-btn confirm danger"
                onClick={() => removerUsuarioMutation.mutate(vinculoParaRemover)}
                disabled={removerUsuarioMutation.isPending}
              >
                {removerUsuarioMutation.isPending ? 'Removendo...' : 'Remover Vínculo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer Ajuda */}
      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Configurações e Integração"
        secoes={helpSections}
      />

      {/* Drawer Ajuda — Defaults do Produto Nasajon (o "?" do card) */}
      <ComoFunciona
        isOpen={isDefaultsHelpOpen}
        onClose={() => setIsDefaultsHelpOpen(false)}
        titulo="Defaults do Produto Nasajon"
        secoes={[{ titulo: 'Para que servem', conteudo: defaultsHelpConteudo }]}
      />

      {isHistoricoOpen && <HistoricoConfiguracaoModal onFechar={() => setIsHistoricoOpen(false)} />}
    </div>
  );
};

export default OrcamentoCiliaConfig;
